import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingDown,
  TrendingUp,
  Search,
  RefreshCcw,
  DollarSign,
  Zap,
  ShieldAlert,
  ChevronRight,
  ChevronDown,
  Filter,
  Download,
  Percent,
  Activity,
  Sparkles,
  ArrowRightLeft,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div<{ $inHub?: boolean }>`
  min-height: ${props => props.$inHub ? 'auto' : 'calc(100vh - 64px)'};
  background: transparent;
  padding: ${props => props.$inHub ? '1rem 0' : '2rem'};
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = 
  ({ label, value, icon, color }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

interface ReplacementSuggestion {
  securityId: number;
  cusip: string;
  securityName: string;
  assetClass: string;
  securityType: string;
  couponRate: number;
  maturityDate: string;
  rationale: string;
  similarityScore: number;
  washSaleCompliant: boolean;
}

const TaxHarvestingScreen: React.FC<{ inHub?: boolean }> = ({ inHub }) => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [threshold, setThreshold] = useState(5.0);
  const [debouncedThreshold, setDebouncedThreshold] = useState(5.0);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [replacements, setReplacements] = useState<Record<number, ReplacementSuggestion[]>>({});
  const [loadingReplacements, setLoadingReplacements] = useState<Set<number>>(new Set());

  // Debounce threshold changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedThreshold(threshold);
    }, 500);
    return () => clearTimeout(handler);
  }, [threshold]);

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/tax-harvesting/opportunities', {
        params: { 
          threshold: debouncedThreshold,
          portfolioId: selectedPortfolioId || undefined
        }
      });
      setOpportunities(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedThreshold, selectedPortfolioId]);

  const fetchPortfolios = useCallback(async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios');
      const data = response.data?.content || response.data || [];
      setPortfolios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const stats = useMemo(() => {
    const totalPotentialLoss = opportunities.reduce((sum, o) => sum + (o.unrealizedLoss || 0), 0);
    const totalMV = opportunities.reduce((sum, o) => sum + (o.marketValue || 0), 0);
    return {
      count: opportunities.length,
      loss: totalPotentialLoss,
      mv: totalMV,
      avgPct: (opportunities.length && totalMV > 0) ? Math.abs(totalPotentialLoss / totalMV * 100).toFixed(1) : '0'
    };
  }, [opportunities]);

  const fetchReplacements = useCallback(async (securityId: number, assetClass?: string) => {
    if (replacements[securityId]) return; // Already loaded

    setLoadingReplacements(prev => new Set(prev).add(securityId));
    try {
      const response = await axios.get(`/api/tax-harvesting/replacements/${securityId}`, {
        params: { assetClass }
      });
      setReplacements(prev => ({
        ...prev,
        [securityId]: Array.isArray(response.data) ? response.data : []
      }));
    } catch (error) {
      console.error('Error fetching replacements:', error);
      setReplacements(prev => ({ ...prev, [securityId]: [] }));
    } finally {
      setLoadingReplacements(prev => {
        const next = new Set(prev);
        next.delete(securityId);
        return next;
      });
    }
  }, [replacements]);

  const toggleExpanded = useCallback((securityId: number, assetClass?: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(securityId)) {
        next.delete(securityId);
      } else {
        next.add(securityId);
        fetchReplacements(securityId, assetClass);
      }
      return next;
    });
  }, [fetchReplacements]);

  return (
    <Container $inHub={inHub}>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <TrendingDown className="w-8 h-8 text-emerald-400" />
                Tax Alpha Optimizer
              </h1>
              <p className="text-slate-500 font-medium mt-1">AI-driven identification of tax-loss harvesting opportunities across portfolios</p>
            </div>
            <div className="flex gap-3">
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">Target Portfolio</label>
                <select 
                  value={selectedPortfolioId} 
                  onChange={(e) => setSelectedPortfolioId(e.target.value)}
                  className="bg-slate-950/50 border border-white/10 rounded-lg px-3 py-1 text-xs text-white focus:outline-none focus:border-blue-500/50 min-w-[180px]"
                >
                  <option value="">All Portfolios</option>
                  {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Loss Threshold %</label>
                <input 
                  type="number" 
                  step="0.1"
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value))}
                  className="w-16 bg-slate-950/50 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500/50"
                />
              </div>
              <button 
                onClick={fetchOpportunities}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
                Scan Market
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Harvestable Opportunities" value={String(stats.count)} icon={<Zap className="w-5 h-5" />} color="#10b981" />
            <StatCard label="Potential Tax Savings" value={`$${(Math.abs(stats.loss) / 1000).toFixed(1)}K`} icon={<DollarSign className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Avg. Loss Intensity" value={`${stats.avgPct}%`} icon={<Percent className="w-5 h-5" />} color="#f59e0b" />
            <StatCard label="Wash Sale Risk" value="Low" icon={<ShieldAlert className="w-5 h-5" />} color="#8b5cf6" />
          </div>
        </motion.div>

        <GlassCard className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-400" />
              Identified Lots
            </h3>
            <button className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">Apply All Harvests</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="pb-3 px-4">Portfolio</th>
                  <th className="pb-3 px-4">Security</th>
                  <th className="pb-3 px-4 text-right">Cost Basis</th>
                  <th className="pb-3 px-4 text-right">Mkt Value</th>
                  <th className="pb-3 px-4 text-right">Unrealized Loss</th>
                  <th className="pb-3 px-4 text-center">Wash Sale</th>
                  <th className="pb-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {opportunities.map((o, i) => {
                  const isExpanded = expandedRows.has(o.securityId);
                  const isLoadingReplacement = loadingReplacements.has(o.securityId);
                  const securityReplacements = replacements[o.securityId] || [];

                  return (
                    <React.Fragment key={i}>
                      <tr className="group hover:bg-white/5 transition-colors border-b border-white/5">
                        <td className="py-4 px-4 font-semibold text-slate-200">{o.portfolioName}</td>
                        <td className="py-4 px-4">
                          <div className="font-bold text-white">{o.securityName}</div>
                          <div className="text-[10px] font-mono text-slate-500 uppercase">{o.cusip}</div>
                        </td>
                        <td className="py-4 px-4 text-right font-mono text-slate-400">${o.costBasis?.toLocaleString()}</td>
                        <td className="py-4 px-4 text-right font-mono text-slate-400">${o.marketValue?.toLocaleString()}</td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-bold text-red-400">-${Math.abs(o.unrealizedLoss)?.toLocaleString()}</span>
                          <div className="text-[10px] font-bold text-red-500/50">({((o.unrealizedLoss / o.costBasis) * 100).toFixed(1)}%)</div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={cn(
                            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                            o.washSaleRisk ? "bg-amber-500/10 text-amber-400" : "bg-emerald-500/10 text-emerald-400"
                          )}>
                            {o.washSaleRisk ? 'Risk Detected' : 'Clear'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => toggleExpanded(o.securityId, o.assetClass)}
                              className={cn(
                                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all",
                                isExpanded
                                  ? "bg-purple-600 text-white"
                                  : "bg-purple-600/20 text-purple-400 hover:bg-purple-600/30"
                              )}
                            >
                              {isLoadingReplacement ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Sparkles className="w-3 h-3" />
                              )}
                              AI Swap
                              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => navigate('/trade-entry/fixed-income', { state: { security: o, action: 'SELL' } })}
                              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold uppercase tracking-widest transition-all shadow-lg shadow-blue-600/10"
                            >
                              Harvest
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* AI Replacement Suggestions Panel */}
                      <AnimatePresence>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="p-0 border-b border-white/5">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-l-4 border-purple-500 p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-4 h-4 text-purple-400" />
                                    <span className="text-sm font-bold text-white">AI-Powered Replacement Suggestions</span>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">Wash-Sale Compliant</span>
                                  </div>

                                  {isLoadingReplacement ? (
                                    <div className="flex items-center justify-center py-8 text-slate-500">
                                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                      Analyzing similar securities...
                                    </div>
                                  ) : securityReplacements.length === 0 ? (
                                    <div className="py-6 text-center text-slate-500 italic">
                                      No replacement suggestions found. Consider ETFs with similar exposure.
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {securityReplacements.map((r, idx) => (
                                        <div
                                          key={idx}
                                          className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5 hover:border-purple-500/30 transition-colors"
                                        >
                                          <div className="flex items-center gap-4">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 text-xs font-bold">
                                              {Math.round(r.similarityScore * 100)}%
                                            </div>
                                            <div>
                                              <div className="flex items-center gap-2">
                                                <span className="font-bold text-white">{r.securityName}</span>
                                                <span className="text-[10px] font-mono text-slate-500 uppercase">{r.cusip}</span>
                                                {r.washSaleCompliant && (
                                                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[9px] font-bold uppercase">
                                                    <CheckCircle className="w-2.5 h-2.5" />
                                                    Safe
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-[11px] text-slate-400 mt-0.5">
                                                {r.couponRate && `${r.couponRate}% coupon`}
                                                {r.maturityDate && ` • Matures ${r.maturityDate}`}
                                                {r.assetClass && ` • ${r.assetClass}`}
                                              </div>
                                              <div className="text-[11px] text-purple-400/80 mt-1 italic">
                                                {r.rationale}
                                              </div>
                                            </div>
                                          </div>
                                          <button
                                            onClick={() => navigate('/trade-entry/fixed-income', {
                                              state: {
                                                sellSecurity: o,
                                                buySecurity: r,
                                                action: 'SWAP'
                                              }
                                            })}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest transition-all"
                                          >
                                            <ArrowRightLeft className="w-3 h-3" />
                                            Swap
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                })}
                {opportunities.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-500 italic">No harvesting opportunities found at {threshold}% threshold</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </div>
    </Container>
  );
};

export default TaxHarvestingScreen;
